/**
 * UsersFeedbackDetailsPage — admin users-feedback details at `/admin/users-feedback/:id`.
 *
 * Web port of Flutter's `UsersFeedbackDetailsScreen`
 * (`lib/presentation/screens/feedback/user_feedback_details_screen.dart`):
 * a "Feedback" app bar (back + centered title) above a scrollable column of
 * the feedback text panel followed — only when screenshots exist — by a
 * 3-column screenshots grid.
 *
 * Route contract:
 * - `:id` is `feedback.id`, kept in the URL so the route is
 *   refresh-survivable, but it is NOT used to fetch — Flutter only ever builds
 *   this screen from the full object. The real payload is the `feedback` in
 *   `location.state` (see `Navigator.navigateToUsersFeedbackDetails`).
 * - On deep link / refresh (no state), the page renders a centered not-found
 *   message; there is no fetch-by-id path.
 *
 * App-bar provenance: the bar is the shared `ExpauseAppBar`
 * (`presentation/components/common/appBar/ExpauseAppBar.tsx`). Flutter builds
 * this screen's bar as a **raw inline `AppBar`** (`user_feedback_details_screen.dart:67-84`)
 * rather than via the shared `BaseAppBar` widget, but its shape is the same one
 * `ExpauseAppBar` ports (`lib/presentation/custom_widgets/base_app_bar.dart:5-42`:
 * back leading + `centerTitle: true` + `kToolbarHeight`). The leading handler is
 * the deep-link-guarded `Navigator.goBackOr` (`presentation.md` → App bar →
 * "Back affordance"), a **superset** of Flutter's `_onBackPress` →
 * `NavigationService.pop()` (`:44`), not a divergence from it: identical whenever
 * there is in-app history, different only on the entry Flutter cannot have at
 * all. That entry matters more here than anywhere else — a deep link renders the
 * not-found body, so the chevron is the page's ONLY control. The bar is rendered
 * deliberately ABOVE the `feedback ? … : …` branch so the not-found
 * (deep-link/refresh) state still has a way out.
 *
 * Moving from the hand-rolled bar to `ExpauseAppBar` makes three intentional
 * visual changes: the title steps from `responsive.font.lg`/`semibold` to
 * `responsive.font.xl`/`bold`, the bar becomes sticky with a blurred
 * 50%-opacity backdrop, and the top safe-area inset is now honoured.
 *
 * Web deviations from Flutter (documented per `presentation.md`, directed by
 * the task prompt):
 * - The feedback text is rendered as **read-only, selectable text**, NOT an
 *   editable input. Flutter wrongly uses an editable `TextField` (with a
 *   200-char limiter + "Your feedback..." hint) for what is display-only
 *   content (`user_feedback_details_screen.dart:110-151`); the web fixes this.
 *   The limiter and hint are input-only concerns and are not ported.
 * - The screenshots grid is the shared `VirtualList` grid (dynamic column
 *   count on web) and each screenshot tap navigates to `DefaultMediaPage`
 *   rather than Flutter's content-list screen. See `FeedbackScreenshotsGrid`.
 */
import { useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { Navigator } from '@/presentation/routing/Navigator';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import type { InAppFeedbackEntity } from '@/domain/entities/feedback/inAppFeedbackEntity';
import { FeedbackScreenshotsGrid } from './components/FeedbackScreenshotsGrid';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface UsersFeedbackLocationState {
    /** The full feedback entity, passed on in-app navigation. */
    feedback?: InAppFeedbackEntity;
}

export const UsersFeedbackDetailsPage = () => {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();
    const { l10n } = useLocale();
    const location = useLocation();

    // `:id` is `feedback.id`, kept for a refresh-survivable URL. It is
    // intentionally NOT read for data — the real payload is
    // `location.state.feedback`.
    useParams<{ id: string }>();

    const locationState = location.state as UsersFeedbackLocationState | undefined;
    const feedback = locationState?.feedback ?? null;

    // Canonical screen-key wiring (mirrors `DefaultMediaPage` /
    // `SupportItemPage`). Capture once on mount so the `KeepAliveOutlet` can
    // disambiguate two pushed instances of the same detail route.
    const { currentScreenKeyRef } = useKeepAlive();
    const screenKeyRef = useRef<string>(
        currentScreenKeyRef.current || generateScreenKey(location.pathname),
    );
    const screenKey = screenKeyRef.current;

    useBasePage({ screenKey, onInit: () => { } });

    // Deep-link-guarded pop. Fallback is the admin feedback list
    // (`/admin/users-feedback`) — the single surface that pushes this route, via
    // `Navigator.navigateToUsersFeedbackDetails` from a list row — so a
    // deep-linked visitor stuck on the not-found body lands on the list the
    // entity actually lives in rather than on a dead chevron.
    const handleBack = () => {
        Navigator.goBackOr(() => Navigator.navigateToUsersFeedback());
    };

    return (
        <div
            {...qaAttr({ id: 'feedback-details', status: 'ready' })}
            style={{
                minHeight: '100vh',
                backgroundColor: theme.background,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* App bar — back button + centered "Feedback" title (mirrors
                Flutter's app bar at user_feedback_details_screen.dart:67-84).
                Deliberately rendered ABOVE the `feedback ? … : …` branch so the
                not-found (deep-link/refresh) state still has a way out. */}
            <ExpauseAppBar
                rootQa={{ id: 'feedback-details-app-bar' }}
                title={l10n.feedback.detailsTitle}
                leadingQa={{ id: 'feedback-details-back' }}
                onLeadingTap={handleBack}
            />

            {/* Body. With no feedback in state (deep link / refresh), render a
                centered not-found message — there is no fetch-by-id path. */}
            {feedback ? (
                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: `${responsive.spacing.base}px`,
                        paddingTop: `${responsive.spacing.base}px`,
                        paddingBottom: `${responsive.spacing.base}px`,
                        overflowY: 'auto',
                    }}
                >
                    {/* Feedback text panel — rounded surface card showing the
                        description as read-only selectable text (NOT an input;
                        the deliberate fix of Flutter's editable-TextField
                        mistake). Mirrors Flutter's `_feedbackWidget` panel at
                        user_feedback_details_screen.dart:110-151. */}
                    <div
                        style={{
                            margin: `0 ${responsive.spacing.base}px`,
                            background: theme.surface,
                            borderRadius: responsive.borderRadius.base,
                            padding: responsive.spacing.base,
                        }}
                    >
                        <p
                            {...qaAttr({ id: 'feedback-details-description' })}
                            style={{
                                margin: 0,
                                color: theme.text.primary,
                                fontSize: responsive.font.base,
                                lineHeight: 1.6,
                                whiteSpace: 'pre-wrap',
                                userSelect: 'text',
                            }}
                        >
                            {feedback.description}
                        </p>
                    </div>

                    {/* Screenshots grid — only when screenshots exist (Flutter
                        `if (screenshots != null)` at :101). */}
                    {feedback.screenshots && (
                        <FeedbackScreenshotsGrid screenshots={feedback.screenshots} />
                    )}
                </div>
            ) : (
                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: `${responsive.spacing.lg}px`,
                    }}
                >
                    <p
                        {...qaAttr({ id: 'feedback-details-not-found' })}
                        style={{
                            margin: 0,
                            textAlign: 'center',
                            color: theme.text.secondary,
                            fontSize: responsive.font.base,
                        }}
                    >
                        {l10n.feedback.notFound}
                    </p>
                </div>
            )}
        </div>
    );
};
